import { useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import type Guid from "_shared/guids/guid";
import { useState } from "react";
import { useNavigate } from "react-router";
import { useSession } from "users/__session/session-context";
import { useLazyGetProposalTemplateByIdQuery } from "work/proposal-templates/_api/proposal-template-api";
import ProposalTemplate from "work/proposal-templates/proposal-template";
import { startDraftFromProposalTemplate } from "work/proposal-templates/start-draft-from-proposal-template";
import { fetchIndividualNames } from "work/proposals/drafts-from-previous/fetch-individual-names";

/**
 * "Create from Template": opens a new, unsaved draft pre-filled from a saved proposal template (#6129).
 *
 * `isStarting` is true while it reads the template again, so the control that started it can show
 * that it is working and cannot be pressed twice.
 */
export default function useStartDraftFromProposalTemplate() {
  const dispatch = useAttorneyHubDispatch();
  const navigate = useNavigate();
  const session = useSession();
  const [getTemplate] = useLazyGetProposalTemplateByIdQuery();
  const [isStarting, setIsStarting] = useState(false);

  async function start(templateId: Guid): Promise<boolean> {
    if (isStarting) return false;
    setIsStarting(true);
    const started = await startDraftFromProposalTemplate({
      templateId,
      session,
      dispatch,
      navigate,
      fetchTemplate: async (id: Guid) =>
        ProposalTemplate.fromJSON(await getTemplate({ templateId: id.value }).unwrap()),
      fetchNames: (userIds: Set<string>) => fetchIndividualNames(dispatch, userIds),
    });
    setIsStarting(false);
    return started;
  }

  return { start, isStarting };
}
